import {
  createListItem,
  deleteListItem,
  getListItems,
  updateListItem,
} from '../graph/lists';
import { LISTS } from './listNames';

/**
 * NotificationConfig rows drive the Phase 4 Power Automate flows (the app
 * NEVER sends email — spec §10). This module + the admin tab manage the
 * rules; docs/flows.md tells the flows how to consume them.
 */

export type NotificationEventType =
  | 'audit_due_soon'
  | 'audit_due_now'
  | 'audit_overdue_reminder'
  | 'audit_escalation'
  | 'task_assigned'
  | 'task_due_soon'
  | 'task_overdue'
  | 'task_escalation'
  | 'task_reopened'
  | 'task_comment';

export type RecipientRule =
  | 'dm'
  | 'auditors'
  | 'dm_and_auditors'
  | 'assignee'
  /** The district's escalation contact (DM supervisor); global fallback below. */
  | 'escalation_contact'
  /** The specific person in customRecipientUpn. */
  | 'custom';

export interface NotificationRule {
  id: string;
  eventType: NotificationEventType;
  /** Days before/after the anchor date; meaning depends on the event. */
  thresholdDays: number | null;
  /** Re-send every N days while the condition persists (reminders). */
  repeatCadenceDays: number | null;
  recipientRule: RecipientRule;
  /** The specific person when recipientRule = 'custom'. */
  customRecipientUpn: string;
  /** GLOBAL escalation fallback — per-district targets live on Districts. */
  escalationTargetUpn: string;
  escalationAfterDays: number | null;
  enabled: boolean;
}

interface ConfigFields {
  Title: string;
  EventType: string;
  ThresholdDays: number | null;
  RepeatCadenceDays: number | null;
  RecipientRule: string;
  CustomRecipientUpn: string;
  EscalationTargetUpn: string;
  EscalationAfterDays: number | null;
  Enabled: boolean;
}

export const EVENT_LABELS: Record<NotificationEventType, string> = {
  audit_due_soon: 'Audit entering its due-soon window',
  audit_due_now: 'Audit due today',
  audit_overdue_reminder: 'Audit overdue (repeating reminder)',
  audit_escalation: 'Audit overdue — escalate',
  task_assigned: 'Task assigned',
  task_due_soon: 'Task due soon',
  task_overdue: 'Task overdue (repeating reminder)',
  task_escalation: 'Task overdue — escalate',
  task_reopened: 'Task reopened',
  task_comment: 'New comment on a task',
};

/**
 * Who each event emails. The recipients are BUILT INTO the flows
 * (provisioning/flows; docs/flows.md) — the rule's stored RecipientRule /
 * CustomRecipientUpn columns are not read by any flow, so the admin screen
 * shows this instead of a picker (owner ruling 2026-10-18). Keep in step
 * with the flows when they change.
 */
export const WHO_GETS_IT: Record<NotificationEventType, string> = {
  audit_due_soon:
    "The store’s DM. Auditor requirements: the auditor group address. Store-team requirements: the store account.",
  audit_due_now:
    "The store’s DM. Auditor requirements: the auditor group address. Store-team requirements: the store account.",
  audit_overdue_reminder:
    "The store’s DM. Auditor requirements: the auditor group address. Store-team requirements: the store account.",
  audit_escalation:
    "The district’s escalation contact (or the global escalation contact below), CC the DM. Auditor requirements: the global escalation contact only. Store-team requirements: the DM, CC the store account.",
  task_assigned: 'The person (or store account) the task is assigned to.',
  task_due_soon: 'The assignee.',
  task_overdue: 'The assignee.',
  task_escalation: "The store’s DM, CC the assignee.",
  task_reopened: 'The assignee — never the person who reopened it.',
  task_comment:
    'The assignee — or the task creator when the assignee wrote the comment; never the author — plus anyone ticked under “Also email”.',
};

export const ALL_EVENT_TYPES = Object.keys(
  EVENT_LABELS,
) as NotificationEventType[];

/**
 * Sensible starting rules ("Add default rules" on the admin tab). Store accounts
 * are mail-enabled (owner confirmed 2026-08-13), so task events go straight
 * to the assignee. Two escalation types (owner ruling 2026-08-13): audits
 * not being done escalate ABOVE the DM (escalation_contact); overdue tasks
 * escalate TO the DM.
 */
export const DEFAULT_RULES: Array<Omit<NotificationRule, 'id'>> = [
  { eventType: 'audit_due_soon', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'dm', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'audit_due_now', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'dm', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'audit_overdue_reminder', thresholdDays: null, repeatCadenceDays: 7, recipientRule: 'dm', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'audit_escalation', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'escalation_contact', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: 14, enabled: true },
  { eventType: 'task_assigned', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'assignee', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'task_due_soon', thresholdDays: 3, repeatCadenceDays: null, recipientRule: 'assignee', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'task_overdue', thresholdDays: null, repeatCadenceDays: 7, recipientRule: 'assignee', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  { eventType: 'task_escalation', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'dm', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: 14, enabled: true },
  { eventType: 'task_reopened', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'assignee', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
  // Flow 2c swaps the recipient when the assignee is the one commenting
  // (the creator gets notified instead) — 'assignee' here is the base rule.
  { eventType: 'task_comment', thresholdDays: null, repeatCadenceDays: null, recipientRule: 'assignee', customRecipientUpn: '', escalationTargetUpn: '', escalationAfterDays: null, enabled: true },
];

function toRule(id: string, f: Partial<ConfigFields>): NotificationRule {
  const eventType = (
    ALL_EVENT_TYPES as readonly string[]
  ).includes(f.EventType ?? '')
    ? (f.EventType as NotificationEventType)
    : 'audit_due_soon';
  const rule: RecipientRule =
    f.RecipientRule === 'auditors' ||
    f.RecipientRule === 'dm_and_auditors' ||
    f.RecipientRule === 'assignee' ||
    f.RecipientRule === 'escalation_contact' ||
    f.RecipientRule === 'custom'
      ? f.RecipientRule
      : 'dm';
  return {
    id,
    eventType,
    thresholdDays: f.ThresholdDays ?? null,
    repeatCadenceDays: f.RepeatCadenceDays ?? null,
    recipientRule: rule,
    customRecipientUpn: f.CustomRecipientUpn ?? '',
    escalationTargetUpn: f.EscalationTargetUpn ?? '',
    escalationAfterDays: f.EscalationAfterDays ?? null,
    // Authorization-ish boolean: missing fails closed (rule disabled).
    enabled: f.Enabled ?? false,
  };
}

function toFields(rule: Omit<NotificationRule, 'id'>): Partial<ConfigFields> {
  return {
    Title: EVENT_LABELS[rule.eventType],
    EventType: rule.eventType,
    ThresholdDays: rule.thresholdDays,
    RepeatCadenceDays: rule.repeatCadenceDays,
    RecipientRule: rule.recipientRule,
    CustomRecipientUpn: rule.customRecipientUpn,
    EscalationTargetUpn: rule.escalationTargetUpn,
    EscalationAfterDays: rule.escalationAfterDays,
    Enabled: rule.enabled,
  };
}

/** All rules, ordered like ALL_EVENT_TYPES (small list, unfiltered fetch). */
export async function getNotificationRules(): Promise<NotificationRule[]> {
  const items = await getListItems<ConfigFields>(LISTS.notificationConfig);
  return items
    .map((i) => toRule(i.id, i.fields))
    .sort(
      (a, b) =>
        ALL_EVENT_TYPES.indexOf(a.eventType) -
        ALL_EVENT_TYPES.indexOf(b.eventType),
    );
}

export async function createNotificationRule(
  rule: Omit<NotificationRule, 'id'>,
): Promise<void> {
  await createListItem<ConfigFields>(LISTS.notificationConfig, toFields(rule));
}

export async function updateNotificationRule(
  rule: NotificationRule,
): Promise<void> {
  await updateListItem<ConfigFields>(
    LISTS.notificationConfig,
    rule.id,
    toFields(rule),
  );
}

export async function deleteNotificationRule(id: string): Promise<void> {
  await deleteListItem(LISTS.notificationConfig, id);
}
